/**
 * DeliveryEst - styled components
 * Light/Dark via data-mode on Container; store type via data-store-type
 * Shell = Container (100% width); Inner = ContentInner (max 1440 centered)
 */

import styled from 'styled-components'
import { ContentInner } from '../../theme/components'

export const Container = styled.div<{ order?: number }>`
  width: 100%;
  border-top: var(--border-regular) solid var(--neutral-border-med);
  border-bottom: var(--border-regular) solid var(--neutral-border-med);
  background: var(--neutral-white);
  order: ${({ order }: { order?: number }) => order || 0};
  &[data-mode='dark'] {
    background: var(--neutral-darkest);
  }
  &[data-flush-top='true'] {
    border-top: none;
  }
  @media (max-width: 767px) {
    &[data-mode='light'] {
      border-color: var(--neutral-muted);
    }
    &[data-mode='dark'] {
      border-top-color: transparent;
      border-bottom-color: transparent;
    }
  }
`

export const Inner = styled(ContentInner)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: var(--spacing-16);
  [data-store-type='closed'] &,
  [data-store-type='gracePeriod'] & {
    padding-top: var(--spacing-24);
    padding-bottom: var(--spacing-24);
  }
  @media (max-width: 1511px) {
    gap: var(--spacing-24);
  }
  @media (max-width: 1279px) {
    [data-store-type='closed'] &,
    [data-store-type='gracePeriod'] & {
      padding-block: var(--spacing-24);
    }
  }
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-8);
    padding-block: var(--spacing-24);
  }
`

export const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-8);
  flex: 0 0 auto;
  @media (max-width: 1511px) {
    flex: 0 1 auto;
    min-width: 0;
  }
  @media (max-width: 767px) {
    flex: none;
    width: 100%;
    align-items: center;
  }
`

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-8);
  padding: var(--spacing-4) var(--spacing-8);
  background: #e3ffee;
  font: 600 12px/20px var(--font-barlow);
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-green);
  [data-store-type='closed'] & {
    background: rgba(200, 16, 46, 0.1);
    color: var(--accent-red);
  }
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }
`

export const Headline = styled.h2`
  margin: 0;
  font: 600 28px/34px var(--font-barlow-semi-condensed);
  text-transform: uppercase;
  color: var(--neutral-darkest);
  white-space: nowrap;
  [data-mode='dark'] & {
    color: var(--neutral-white);
  }
  [data-store-type='closed'] &,
  [data-store-type='gracePeriod'] & {
    text-transform: none;
  }
  @media (max-width: 1511px) {
    white-space: normal;
  }
  @media (max-width: 767px) {
    width: 100%;
    text-align: center;
    [data-store-type='closed'] &,
    [data-store-type='gracePeriod'] & {
      font: 600 28px/34px var(--font-barlow-semi-condensed);
      letter-spacing: 0;
    }
  }
`

export const HeadlineDate = styled.span`
  color: var(--accent-red);
  [data-show='desktop'] {
    display: inline;
  }
  [data-show='mobile'] {
    display: none;
  }
  [data-store-type='closed'] &,
  [data-store-type='gracePeriod'] & {
    display: inline;
  }
  @media (max-width: 767px) {
    display: block;
    [data-show='desktop'] {
      display: none;
    }
    [data-show='mobile'] {
      display: inline;
    }
  }
`

export const ContactLine = styled.p`
  margin: 0;
  font: 400 14px/22px var(--font-inter);
  color: var(--neutral-body);
  @media (max-width: 767px) {
    width: 100%;
    text-align: center;
  }
  [data-mode='dark'] & {
    color: var(--neutral-muted);
  }
`

export const DatesBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-8);
  flex: 0 0 auto;
  @media (max-width: 767px) {
    flex-direction: row;
    align-items: center;
    gap: 16px;
    width: 100%;
    [data-store-type='ondemand'] & {
      gap: var(--spacing-24);
      justify-content: center;
    }
  }
  .date-tiles {
    display: flex;
    align-items: center;
    gap: var(--spacing-32);
  }
`

export const DateTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-4);
  white-space: nowrap;
  @media (max-width: 767px) {
    width: 64px;
    min-width: 64px;
    max-width: 64px;
    flex-shrink: 0;
    overflow: visible;
  }
`

export const DateTileLabel = styled.span`
  font: 600 12px/20px var(--font-barlow);
  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
  color: var(--neutral-secondary);
  [data-mode='dark'] & {
    color: var(--neutral-border-med);
  }
  [data-show='desktop'] {
    display: inline;
  }
  [data-show='mobile'] {
    display: none;
  }
  @media (max-width: 767px) {
    display: block;
    width: max-content;
    max-width: none;
    min-width: 0;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    [data-show='desktop'] {
      display: none;
    }
    [data-show='mobile'] {
      display: inline;
    }
  }
`

export const DateCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 64px;
  padding: var(--spacing-4) var(--spacing-16);
  background: var(--neutral-darkest);
  color: var(--neutral-white);
  [data-mode='dark'] & {
    background: var(--neutral-white);
    color: var(--neutral-darkest);
  }
  &[data-variant='arrival'] {
    background: var(--accent-red);
    color: var(--neutral-white);
  }
  .month {
    font: 600 12px/20px var(--font-barlow);
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .day {
    font: 600 32px/38px var(--font-barlow-semi-condensed);
    text-transform: capitalize;
  }
`

export const CloseTime = styled.p`
  margin: 0;
  font: 400 12px/20px var(--font-inter);
  text-align: center;
  color: var(--neutral-secondary);
  [data-mode='dark'] & {
    color: var(--neutral-white);
  }
  [data-store-type='ondemand'] & {
    display: none;
  }
  strong {
    font-weight: 700;
  }
  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    text-align: center;
    [data-mode='dark'] & {
      color: var(--neutral-border-med);
    }
    [data-store-type='ondemand'] & {
      display: flex;
      flex: none;
      width: 141px;
    }
    strong {
      font-weight: 600;
    }
    .comma {
      display: none;
    }
  }
`

export const CtaWrap = styled.div`
  flex: 0 0 auto;
  white-space: nowrap;
  > div {
    width: auto;
    white-space: nowrap;
  }
  .anticon {
    font-size: 14px;
  }
  @media (max-width: 767px) {
    display: none;
  }
`



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/components/DeliveryEst/styledComponents.tsx